import React, { useState } from "react";
import demoThumbnail from "@/assets/lanerunner-demo-thumbnail.png.asset.json";
import sectionBg from "@/assets/see-it-in-action-bg.png.asset.json";

/**
 * Homepage Section 5 — See It In Action.
 * The only full-width dark section below the hero; reuses the hero palette.
 * Shows the full shipper-demo thumbnail + play button; the YouTube iframe
 * loads only when the user clicks to play.
 */

const VIDEO_EMBED_URL = "https://www.youtube.com/embed/wp6kuorXAKw?autoplay=1&rel=0&modestbranding=1";

const HomeSeeItInAction: React.FC = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="see-it-in-action"
      className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-20 sm:py-28"
      style={{ backgroundImage: `url(${sectionBg.url})` }}
    >
      {/* Gradient scrim: darkest at the top where the heading/copy sit,
          lighter toward the bottom so the wave graphic stays visible. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-[#06131A]/85 via-[#06131A]/50 to-[#06131A]/30"
      />

      <div className="relative mx-auto max-w-5xl px-6 text-center">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#3ED9BE]">
          See It In Action
        </span>
        <h2 className="mt-4 text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-[42px]">
          The Full Shipper Walkthrough
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-[16.5px] leading-relaxed text-[#8FA5A2]">
          Everything you need to know in one walkthrough — post a load, get quotes, compare the market, award freight, and track it to delivery. This is the complete demo.
        </p>

        <div className="mt-12">
          <div className="relative mx-auto aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0A1A22] shadow-[0_40px_90px_-40px_rgba(0,0,0,0.9)]">
            {playing ? (
              <iframe
                className="absolute inset-0 h-full w-full"
                src={VIDEO_EMBED_URL}
                title="LaneRunner full shipper demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <>
                {/* Cover art lives outside the <button>: iOS Safari does not
                    reliably paint absolutely-positioned children of a button,
                    which left the poster blank on mobile. */}
                <img
                  src={demoThumbnail.url}
                  alt="LaneRunner full shipper demo preview"
                  width={1672}
                  height={941}
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-[#06131A]/70 via-transparent to-transparent"
                />
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  aria-label="Play the full LaneRunner demo"
                  className="group absolute inset-0 flex items-center justify-center bg-black/5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#00CFC4]/60"
                >
                  <span
                    aria-hidden
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-[#02857E] shadow-[0_12px_40px_-12px_rgba(2,133,126,0.8)] ring-2 ring-white/30 transition duration-200 ease-out group-hover:scale-110 group-hover:bg-[#027068] sm:h-20 sm:w-20"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="ml-1 h-7 w-7 text-white sm:ml-1.5 sm:h-9 sm:w-9">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </button>
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-white/[0.06] px-4 py-3 text-[11px] text-[#8FA5A2]"
                >
                  <span className="font-semibold text-white/80">LaneRunner full shipper demo</span>
                  <span>Full demo</span>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeSeeItInAction;
